import { createEffect, createRoot, on } from 'solid-js';
import { SectionPlane, SectionBox } from '@data-access/connect/tc-tcps';
import {
  Box3,
  ClipBoxTool,
  ClipPlane,
  ClipPlaneTool,
  Quaternion,
  Vector3,
  Web3DViewer,
} from '@technology/web3d';
import { TrimbimModel } from '@technology/web3d-plugin-trimbim';
import {
  CapabilityProviderBase,
  convertConnectSectionBoxToViewerClipBox,
  convertConnectSectionPlaneToViewerClipPlane,
  convertViewerClipPlaneToConnectSectionPlane,
  convertViewerClipBoxToConnectSectionBox,
  SnapType,
} from '@trimble/web3d-providers';
import { SingleCapabilityBase } from './base/single-capability-base';
import { CapabilityCategory } from './base/models/capability-category';

/** Padding added to the clip box size when created from selection (in world units). */
const CLIP_BOX_FROM_SELECTION_PADDING = 0.5;

/**
 * Handles interactive creation, loading, and management of clip planes
 * and clip boxes for a single viewer.
 */
export class ClippingCapability extends SingleCapabilityBase {
  public override category: CapabilityCategory = CapabilityCategory.Markup;
  private _makeClipPlaneVertical = false;
  private readonly clipPlaneTool: ClipPlaneTool;
  private readonly clipBoxTool: ClipBoxTool;
  private _disposeClearClippingsWhenEmpty?: () => void;
  private savedSnapTypes: number[] | undefined;

  constructor(
    viewer: Web3DViewer,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    providers: CapabilityProviderBase<any>[] = []
  ) {
    super(viewer);
    this.clipPlaneTool = this.viewer.tools.get(ClipPlaneTool)!;
    this.clipBoxTool = this.viewer.tools.get(ClipBoxTool)!;
    this.viewer.addEventListener(
      'clipplaneadded',
      this.onClipPlaneAdded as EventListener
    );
    this.viewer.addEventListener(
      'clipboxadded',
      this.onClipBoxAdded as EventListener
    );
    if (providers.length > 0) {
      this._disposeClearClippingsWhenEmpty = createRoot((dispose) => {
        createEffect(
          on(
            () => providers.every((p) => !p.hasItems()),
            (allEmpty) => {
              if (allEmpty) {
                this.clearAll();
              }
            },
            { defer: true }
          )
        );
        return dispose;
      });
    }
  }

  protected override trackIsEnabled(): void {
    this.enable();
  }

  public override deactivate(): boolean {
    const wasDeactivated = super.deactivate();
    if (wasDeactivated) {
      this._makeClipPlaneVertical = false;
      this.disableClipTools();
      this.restoreSnapTypes();
    }
    return wasDeactivated;
  }

  public override cleanup(): Promise<void> {
    this._makeClipPlaneVertical = false;
    this.restoreSnapTypes();
    this._disposeClearClippingsWhenEmpty?.();
    this._disposeClearClippingsWhenEmpty = undefined;
    this.viewer.removeEventListener(
      'clipplaneadded',
      this.onClipPlaneAdded as EventListener
    );
    this.viewer.removeEventListener(
      'clipboxadded',
      this.onClipBoxAdded as EventListener
    );
    return Promise.resolve();
  }

  private disableClipTools(): void {
    this.clipPlaneTool.enabled = false;
    this.clipBoxTool.enabled = false;
  }

  private setClipSnapTypes(): void {
    if (this.savedSnapTypes === undefined) {
      this.savedSnapTypes = [...this.viewer.settings.snapTypes];
    }
    this.viewer.settings.snapTypes = [
      SnapType.FACE as number,
      SnapType.LINE as number,
    ];
  }

  private restoreSnapTypes(): void {
    if (this.savedSnapTypes !== undefined) {
      this.viewer.settings.snapTypes = this.savedSnapTypes;
      this.savedSnapTypes = undefined;
    }
  }

  private resetInteractiveCreation(): void {
    if (!this.isActive()) return;
    this._makeClipPlaneVertical = false;
    this.deactivate();
  }

  private readonly onClipPlaneAdded = (e: CustomEvent<ClipPlane>): void => {
    if (this._makeClipPlaneVertical) {
      e.detail.makeVertical();
    }
    this.resetInteractiveCreation();
  };

  private readonly onClipBoxAdded = (): void => {
    this.resetInteractiveCreation();
  };

  /**
   * Start interactive clip plane creation (single-shot).
   */
  public startClipPlaneCreation(): void {
    this._makeClipPlaneVertical = false;
    this.disableClipTools();
    this.setClipSnapTypes();
    this.activate();
    this.clipPlaneTool.enabled = true;
  }

  /**
   * Start interactive vertical clip plane creation (single-shot).
   * The placed clip plane will be made vertical automatically.
   */
  public startVerticalClipPlaneCreation(): void {
    this._makeClipPlaneVertical = true;
    this.disableClipTools();
    this.setClipSnapTypes();
    this.activate();
    this.clipPlaneTool.enabled = true;
  }

  /**
   * Start interactive clip box creation (single-shot).
   */
  public startClipBoxCreation(): void {
    this.disableClipTools();
    this.setClipSnapTypes();
    this.activate();
    this.clipBoxTool.enabled = true;
  }

  /**
   * Add a clip plane to the viewer.
   */
  public addClipPlane(
    position: Vector3,
    normal: Vector3,
    source?: string
  ): void {
    this.clipPlaneTool.add(position, normal, undefined, source);
  }

  /**
   * Add a clip box to the viewer.
   */
  public addClipBox(
    position: Vector3,
    quaternion: Quaternion,
    size: Vector3,
    source?: string
  ): void {
    this.clipBoxTool.add(position, quaternion, size, source);
    this.clipBoxTool.deselectClipBox();
  }

  /**
   * Add a clip box fitted to the current viewer selection (or the given selection).
   * Uses the bounding box of the selected entities (Trimbim models only); adds padding to the size.
   * If a clip box already exists, clears it and returns (toggle-off behavior).
   *
   * @param selection - Optional map of modelId → entityIds. If omitted, uses viewer.selection.
   * @returns Promise that resolves to true if a clip box was added, false if cleared or no valid selection.
   */
  public async addClipBoxFromSelection(
    selection?: Map<string, number[]>
  ): Promise<boolean> {
    const existing = this.clipBoxTool.getClipBoxes() ?? [];
    if (existing.length > 0) {
      this.clipBoxTool.clear();
      return false;
    }

    const entries =
      selection ?? new Map(Array.from(this.viewer.selection.entries()));
    if (entries.size === 0) {
      return false;
    }

    const bb = new Box3();
    for (const [modelId, entityIds] of entries) {
      const model = this.viewer.getModel(modelId);
      if (model instanceof TrimbimModel && entityIds.length > 0) {
        const bbe = await model.getBoundingBox(entityIds);
        bb.union(bbe);
      }
    }

    if (bb.isEmpty()) {
      return false;
    }

    const position = new Vector3();
    bb.getCenter(position);

    const size = new Vector3();
    bb.getSize(size);
    size.addScalar(CLIP_BOX_FROM_SELECTION_PADDING);

    const clipBox = this.clipBoxTool.add(
      position,
      new Quaternion(),
      size,
      'selection'
    );
    clipBox.select();

    return true;
  }

  /**
   * Clear all clip planes and clip boxes from the viewer.
   */
  public clearAll(): void {
    this.clipPlaneTool.clear();
    this.clipBoxTool.clear();
  }

  /**
   * Get current clip planes as Connect section planes.
   */
  public getSectionPlanes(): SectionPlane[] {
    const clipPlanes = this.clipPlaneTool.getClipPlanes() ?? [];
    return clipPlanes.map((cp) =>
      convertViewerClipPlaneToConnectSectionPlane(cp.coplanarPoint, cp.normal)
    );
  }

  /**
   * Get current clip box as Connect section box, if one exists.
   */
  public getSectionBox(): SectionBox | undefined {
    const clipBoxes = this.clipBoxTool.getClipBoxes() ?? [];
    if (clipBoxes.length === 0) {
      return undefined;
    }
    const clipBox = clipBoxes[0];
    const size = new Vector3();
    clipBox.getBox().getSize(size);
    return convertViewerClipBoxToConnectSectionBox(
      clipBox.position,
      clipBox.quaternion,
      size
    );
  }

  /**
   * Load clipping from view data.
   * Clears any existing clipping before applying.
   */
  public loadClippingFromView(
    clipPlanes?: { position: Vector3; normal: Vector3 }[],
    clipBox?: { position: Vector3; quaternion: Quaternion; size: Vector3 }
  ): void {
    this.resetInteractiveCreation();
    this.clearAll();

    if (clipPlanes && clipPlanes.length > 0) {
      for (const clipPlane of clipPlanes) {
        this.addClipPlane(clipPlane.position, clipPlane.normal, 'view');
      }
    }

    if (clipBox) {
      this.addClipBox(
        clipBox.position,
        clipBox.quaternion,
        clipBox.size,
        'view'
      );
    }
  }

  /**
   * Convert Connect section planes/box to viewer format and load them.
   * Clears any existing clipping before applying.
   */
  public loadClippingFromConnectData(
    sectionPlanes?: SectionPlane[],
    sectionBox?: SectionBox | null
  ): void {
    const clipPlanes = sectionPlanes?.map(
      convertConnectSectionPlaneToViewerClipPlane
    );
    const clipBox = sectionBox
      ? convertConnectSectionBoxToViewerClipBox(sectionBox)
      : undefined;
    this.loadClippingFromView(clipPlanes, clipBox);
  }
}
